import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import LastMileInfo from "../../../../components/LastMileInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
// helpers
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
// hooks / actions
import { useActions } from "../../../../common/hooks/useActions";
import { catActionCreators } from "../catModule";
import { type CatOffer } from "../types";

interface CatSummaryProps {
  onBuyTickets: () => void;
  cancellationPolicy?: string | null;
  lastMileDestinationName?: string | null;
}

export default function CatSummary({
  onBuyTickets,
  cancellationPolicy,
  lastMileDestinationName = null
}: CatSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";
  const [checkPhoneValidation, setCheckPhoneValidation] = useState(false);

  // states
  const platformType = useSelector((state: any) => state.global.platformType);
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const selectedOffer = useSelector((state: any) => state.cat.selectedOffer) as CatOffer | null;
  const dataForBooking = useSelector((state: any) => state.cat.dataForBooking) as any;
  const errorField = useSelector((state: any) => state.errorField.errorField) as any;
  const catBookingError = useSelector((state: any) => state.cat.bookingError) as boolean;
  const catBookingErrorMessage = useSelector((state: any) => state.cat.bookingErrorMessage) as any[];
  const catEstimation = useSelector((state: any) => state.cat.catEstimation);
  const catEstimationError = useSelector((state: any) => state.cat.catEstimationError) as boolean;
  const lastMileRoute = useSelector((state: any) => state.cat.lastMileRoute);
  const isLastMileRouteLoading = useSelector((state: any) => state.cat.isLastMileRouteLoading);
  const lastMileRouteError = useSelector((state: any) => state.cat.lastMileRouteError);

  // actions
  const updateFormForBooking = useActions(catActionCreators?.updateFormForBookingAction, []);

  const isAirline = platformType === "airline";
  const showLastMile = Boolean(lastMileDestinationName);
  const showMilesAndMore = shouldShowMilesAndMore(catEstimation?.miles_and_more, isAirline);

  useEffect(() => {
    if (catBookingErrorMessage) {
      const hasPhoneField = catBookingErrorMessage.some((f: any) => f.field === "phone");
      setCheckPhoneValidation(hasPhoneField);
    }
  }, [catBookingErrorMessage]);

  const isOfferPricingMissing = useMemo(() => {
    const prices = selectedOffer?.offer_prices;
    return !Array.isArray(prices) || prices.length === 0;
  }, [selectedOffer]);

  const hasKnownPrice = Boolean(catEstimation?.formatted_price);

  const handleBookingFormInputChange = (e: any, input: any) => {
    switch (input) {
      case "nameForm":
        updateFormForBooking({ ...dataForBooking, nameForm: e.target.value });
        break;
      case "emailForm":
        updateFormForBooking({ ...dataForBooking, emailForm: e.target.value });
        break;
      case "mobileNumberForm":
        updateFormForBooking({ ...dataForBooking, mobileNumberForm: e.target.value });
        break;
      case "commentForm":
        updateFormForBooking({ ...dataForBooking, commentForm: e.target.value });
        break;
      default:
        break;
    }
  };

  const renderBookErrorMsg = () => {
    if (!catBookingErrorMessage || catBookingErrorMessage?.length === 0) {
      return (
        <Box mt={1} className="cat__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      );
    }

    return catBookingErrorMessage?.map((error: any) => (
      <Box mt={1} className="cat__error-msg" key={error?.field || Math.random()}>
        {error?.errorMessage ? `${error.errorMessage}` : t("transport.prebookCustomError")}
      </Box>
    ));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} pb={3}>
      {isOfferPricingMissing && (
        <Box mt={1} className="cat__error-msg">
          {t("transport.catPricingMissing")}
        </Box>
      )}

      {catEstimationError && !isOfferPricingMissing && (
        <Box mt={1} className="cat__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      )}

      <Grid container mt={2} spacing={2}>
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", margin: "15px 0 30px 0" }}>
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {catEstimation?.formatted_price || ""}
            </Box>
          </Box>

          <Grid container width={{ sm: "100%", md: "100%" }}>
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestNameLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"nameForm"}
                  type="string"
                  value={dataForBooking?.nameForm}
                  onChange={event => handleBookingFormInputChange(event, "nameForm")}
                  error={errorField?.nameForm}
                  dataAttribute="cat-name"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestEmailLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"emailForm"}
                  type="string"
                  value={dataForBooking?.emailForm}
                  onChange={event => handleBookingFormInputChange(event, "emailForm")}
                  error={errorField?.emailForm}
                  dataAttribute="cat-email"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestPhoneLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"mobileNumberForm"}
                  type="string"
                  value={dataForBooking?.mobileNumberForm}
                  onChange={event => handleBookingFormInputChange(event, "mobileNumberForm")}
                  error={errorField?.mobileNumberForm || checkPhoneValidation}
                  dataAttribute="cat-phone"
                />
                <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("phoneNumberLabel")}</Box>
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"commentForm"}
                  type="string"
                  value={dataForBooking?.commentForm}
                  onChange={event => handleBookingFormInputChange(event, "commentForm")}
                  error={errorField?.commentForm}
                  dataAttribute="cat-comment"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {(showLastMile || showMilesAndMore) && (
          <Grid item xs={12} md={6} pl={{ xs: 0, md: 5 }} pt={{ xs: 2, md: 0 }}>
            {showLastMile ? (
              <Box ml={{ xs: 0, md: 3 }} mr={{ xs: 0, md: 3 }}>
                <LastMileInfo
                  title={t("transport.lastMile.title")}
                  icon={<DirectionsWalkIcon fontSize="small" />}
                  destinationName={lastMileDestinationName as string}
                  distanceMeters={lastMileRoute?.distance_meters ?? null}
                  durationSeconds={lastMileRoute?.duration_seconds ?? null}
                  googleMapsUrl={lastMileRoute?.google_maps_url ?? null}
                  isLoading={isLastMileRouteLoading}
                  error={lastMileRouteError}
                />
              </Box>
            ) : null}

            {showMilesAndMore ? (
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  display: "flex",
                  pb: { xs: 0, md: 6 },
                  pt: { xs: showLastMile ? 2 : 4, md: showLastMile ? 2 : 0 }
                }}
              >
                <MilesAndMoreInfo miles={catEstimation?.miles_and_more} />
              </Box>
            ) : null}
          </Grid>
        )}
      </Grid>

      {catBookingError && renderBookErrorMsg()}

      {cancellationPolicy && (
        <Box pt={6}>
          <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
        </Box>
      )}

      <Grid
        item
        xs={12}
        sx={{
          width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
          margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "25px auto 0 auto",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 2 }
        }}
      >
        {isAirline && (
          <AppButton
            label={t("transport.goBack")}
            onClick={() => navigateBackOrToMain(navigate, { language })}
            disableRipple={true}
            customBackgroundColor={theme.palette.primary.dark}
            customHoverColor={theme.palette.primary.dark}
            fontSize="14px"
            dataAttribute="cat-back"
          />
        )}
        <AppButton
          label={t("transport.buyTicketsBtn")}
          onClick={onBuyTickets}
          disableRipple={true}
          customBackgroundColor={tenantInfo?.style?.button?.color}
          customActiveColor={tenantInfo?.style?.button?.active}
          customHoverColor={tenantInfo?.style?.button?.hover}
          customClickColor={tenantInfo?.style?.button?.click}
          fontSize="14px"
          disabled={isOfferPricingMissing || !hasKnownPrice}
          dataAttribute="cat-buy-tickets"
        />
      </Grid>
    </Box>
  );
}
